import { ArrowLeftOutlined, UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Upload,
} from "antd";

import "./PostItemPage.scss";
import { store } from "../../store/index.store";
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import type { UploadFile, UploadProps } from "antd";
import type { RcFile } from "antd/es/upload";
import { uploadImage } from "../../api/image";
import { useTranslation } from "react-i18next";
import { APIProducts, getProductById } from "../../api/products";
import { useForm } from "antd/lib/form/Form";
import { getImageURL } from "../../common/utils/imageURLgetter";

const onFinishFailed = (errorInfo: any) => {
  console.log("Failed:", errorInfo);
};

const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const PostItemPage = observer(() => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = useForm();

  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const { id } = useParams();

  const isEditMode = !!id;

  useEffect(() => {
    if (id) {
      getProductById(id).then((product: APIProducts) => {
        Object.keys(product.productObj).forEach((key) => {
          console.log(key);
          if (key === "images" && product?.productObj?.images) {
            console.log("Setting file list");
            const images = product?.productObj?.images.map(
              (item: string, index) => ({
                uid: item,
                name: `${form.getFieldValue("title")}-${index}`,
                status: "done",
                url: getImageURL(item, false),
              })
            );

            //@ts-ignore - type incompatible, but it works
            setFileList(images);
          }

          //@ts-ignore
          form.setFieldValue(key, product.productObj[key]);
        });
      });
    }
  }, [id]);

  const handleChange: UploadProps["onChange"] = (info) => {
    let newFileList = [...info.fileList];

    // 1. Limit the number of uploaded files
    // Only to show two recent uploaded files, and old ones will be replaced by the new
    newFileList = newFileList.slice(-3);

    // 2. Read from response and show file link
    newFileList = newFileList.map((file) => {
      if (file.response) {
        // Component will show file.url as link
        file.url = file.response.url;
      }
      return file;
    });

    setFileList(newFileList);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1)
    );
  };

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { productStore, UIStore, loaderStore } = store;

  useEffect(() => {
    UIStore.loadCategories();
  }, []);

  const onFinish = async (values: any) => {
    console.log("Form values:", values);

    if (isEditMode) {
      await productStore.editItem({
        values: {
          ...values,
          images: fileList.map(
            (item: any) =>
              //@ts-ignore
              new URL(item.url).pathname.replace("/", "").split(".")[0]
          ),
        },
        id,
      });
    } else {
      await productStore.postNewItem({
        values: {
          ...values,
          images: fileList.map(
            (item: any) =>
              //@ts-ignore
              new URL(item.url).pathname.replace("/", "").split(".")[0]
          ),
        },
      });
    }

    setIsModalOpen(true);
  };

  const handleCancel = () => setPreviewOpen(false);

  function handleOKSuccesModal() {
    setIsModalOpen(false);
    navigate("/home-page");
  }

  useEffect(() => {
    console.log(fileList);
  }, [fileList]);

  return (
    <div className="p-post-item-page">
      <Modal
        className="p-post-item-page__confirmation-modal"
        title="Uploadd successfull"
        open={isModalOpen}
        onOk={handleOKSuccesModal}
      >
        <p>Your product was successfully posted</p>
        <p>Check your profile to see it</p>
      </Modal>
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
      >
        <img alt="example" style={{ width: "100%" }} src={previewImage} />
      </Modal>
      <div className="p-post-item-page__header" onClick={() => navigate(-1)}>
        <ArrowLeftOutlined className="p-post-item-page__header-back" />
        <h2 className="p-post-item-page__header-title">Post item</h2>
      </div>

      <Form
        name="basic"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        initialValues={{ remember: true }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        className="p-post-item-page__form"
        form={form}
      >
        <Form.Item
          label={t("postItem.title")}
          name="title"
          rules={[
            { required: true, message: "You cannot let this field empty" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={t("postItem.picture")}
          name="images"
          rules={[
            { required: true, message: "You cannot let this field empty" },
          ]}
        >
          <Upload
            customRequest={async (options) => {
              const { onSuccess, onError, file } = options;

              const fmData = new FormData();
              const config = {
                headers: { "content-type": "multipart/form-data" },
              };
              fmData.append("image", file);
              try {
                const { data } = await uploadImage(fmData, config);
                const newLocation = data.data;

                //@ts-ignore
                onSuccess({ ...file, url: newLocation });
              } catch (err) {
                //@ts-ignore
                onError({ event: err });
              }
            }}
            onChange={handleChange}
            multiple={true}
            fileList={fileList}
            onPreview={handlePreview}
          >
            <Button style={{ borderRadius: 10 }} icon={<UploadOutlined />}>
              Click to Upload
            </Button>{" "}
          </Upload>
        </Form.Item>
        <Form.Item
          label={t("postItem.condition")}
          name="condition"
          rules={[
            { required: true, message: "You cannot let this field empty" },
          ]}
        >
          <Select style={{ height: "40px" }}>
            <Select.Option value="new">New</Select.Option>
            <Select.Option value="used">Used</Select.Option>
          </Select>
        </Form.Item>

        <Spin spinning={loaderStore.isLoadingCategories}>
          <Form.Item
            label="Category"
            name="category"
            rules={[
              { required: true, message: "You cannot let this field empty" },
            ]}
          >
            <Select>
              {UIStore?.categories?.map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Spin>

        <Form.Item
          label={t("postItem.price")}
          name="price"
          rules={[
            { required: true, message: "You cannot let this field empty" },
          ]}
        >
          <InputNumber
            addonBefore=""
            addonAfter=""
            placeholder="Pi"
            min={0}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          label={t("postItem.description")}
          name="description"
          rules={[
            { required: true, message: "You cannot let this field empty" },
          ]}
        >
          <Input.TextArea style={{ height: 60 }} />
        </Form.Item>

        <Form.Item
          name="remember"
          valuePropName="checked"
          rules={[
            {
              required: true,
              message: "Please agree with the terms and conditions",
            },
          ]}
          wrapperCol={{ offset: 1, span: 16 }}
        >
          <Checkbox>{t("postItem.agree")}</Checkbox>
        </Form.Item>

        <Form.Item
          name="promote"
          valuePropName="promote"
          wrapperCol={{ offset: 1, span: 16 }}
        >
          <Checkbox>{t("postItem.remember")}</Checkbox>
        </Form.Item>

        {isEditMode && (
          <Form.Item
            name="updateLocation"
            valuePropName="updateLocation"
            wrapperCol={{ offset: 1, span: 16 }}
          >
            <Checkbox>{t("postItem.updateLocation")}</Checkbox>
          </Form.Item>
        )}

        <p>{t("postItem.note")}</p>

        <Form.Item wrapperCol={{ offset: 1, span: 100 }}>
          <Button
            type="primary"
            htmlType="submit"
            style={{ width: "90%", borderRadius: "10px", height: "40px" }}
          >
            {isEditMode ? "Edit item" : t("postItem.post")}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
});
